import { useReadContracts } from "wagmi";
import { type Address } from "viem";
import { type ChainType } from "@/constants/chain-specific";
import { erc721Abi } from "viem";
import { useMemo, useRef, useEffect } from "react";
import type { TokenBalance } from "@/lib/blockscout-api";
import { multicall3Abi } from "viem";
import { chainSettings } from "@/constants/chain-specific/chains";
import { getMarketplaceCollections } from "@/constants/marketplace/collections";
import { shibarium } from "viem/chains";

// Extended ABI for enumerable NFT contracts
const erc721EnumerableAbi = [...erc721Abi, {
  inputs: [{
    name: 'owner',
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;

// Cache structure with optimistic updates
interface CacheEntry {
  balance: number;
  timestamp: number;
  optimisticValue?: number; // For optimistic updates
}
const BATCH_SIZE = 50; // Increased batch size since we're using multicall
const CACHE_TTL = 300000; // Cache TTL in ms (5 minutes) - longer to prevent frequent resets
const GLOBAL_CACHE = new Map<string, CacheEntry>(); // Global cache shared between hook instances
const DEBOUNCE_INTERVAL = 100; // Debounce interval for batch processing

interface NFTCollectionBalance {
  contractAddress: Address;
  apiBalance: number;
  realTimeBalance: number;
  hasDiscrepancy: boolean;
  isLoading: boolean;
  enabled: boolean;
}
interface UseNFTCollectionBalanceVerificationResult {
  verifiedBalances: Map<Address, NFTCollectionBalance>;
  hasAnyDiscrepancy: boolean;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Hook to verify NFT collection balances from API against real-time on-chain balances
 * This ensures the Portfolio drawer shows accurate and current NFT collection counts
 * Supports both ERC721 and ERC1155 collections with optimized multicall batching
 */
export const useNFTCollectionBalanceVerification = (erc721Collections: TokenBalance[] | undefined, erc1155Collections: TokenBalance[] | undefined, userAddress: Address | undefined, chainId: ChainType, enabled: boolean = true): UseNFTCollectionBalanceVerificationResult => {
  // Keep track of batches being processed
  const batchIndexRef = useRef(0);
  const processingTimeoutRef = useRef<ReturnType<typeof setTimeout>>(setTimeout(() => {}, 0));

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  // Combine all NFT collections with deduplication
  const allCollections = useMemo(() => {
    const collections = new Map<string, TokenBalance>();
    if (erc721Collections) {
      erc721Collections.forEach(collection => {
        const addr = collection?.token?.address;
        const addrLower = typeof addr === 'string' ? addr.toLowerCase() : undefined;
        if (addrLower) {
          collections.set(addrLower, collection);
        }
      });
    }
    if (erc1155Collections) {
      erc1155Collections.forEach(collection_0 => {
        const addr_0 = collection_0?.token?.address;
        const addrLower_0 = typeof addr_0 === 'string' ? addr_0.toLowerCase() : undefined;
        if (addrLower_0) {
          collections.set(addrLower_0, collection_0);
        }
      });
    }
    return Array.from(collections.values());
  }, [erc721Collections, erc1155Collections]);

  // Extract contract addresses from NFT collections
  // For Shibarium (chainId 109), also include all marketplace collection addresses
  // since the Blockscout API indexer can be behind and miss some collections
  const contractAddresses = useMemo(() => {
    const addressSet = new Set<string>();

    // Add addresses from API collections
    if (allCollections && allCollections.length > 0) {
      allCollections.forEach(collection_1 => {
        const addr_1 = collection_1?.token?.address;
        if (typeof addr_1 === 'string' && addr_1.length > 0) {
          addressSet.add(addr_1.toLowerCase());
        }
      });
    }

    // For Shibarium, add all marketplace collection addresses to ensure we check them
    if (chainId === shibarium.id) {
      const marketplaceCollections = getMarketplaceCollections(chainId);
      marketplaceCollections.forEach(collection_2 => {
        if (collection_2.address) {
          addressSet.add(collection_2.address.toLowerCase());
        }
      });
    }
    return Array.from(addressSet) as Address[];
  }, [allCollections, chainId]);

  // Split contracts into batches with multicall configuration
  const contractBatches = useMemo(() => {
    if (!userAddress || contractAddresses.length === 0) return [];
    const batches = [];
    const now = Date.now();
    const chainSetting = chainSettings[chainId];
    const multicallAddress = chainSetting && 'multicall' in chainSetting ? chainSetting.multicall : undefined;
    if (!multicallAddress) {
      console.warn(`No multicall address found for chain ${chainId}`);
      return [];
    }
    for (let i = 0; i < contractAddresses.length; i += BATCH_SIZE) {
      const batchAddresses = contractAddresses.slice(i, i + BATCH_SIZE);
      const batchContracts = batchAddresses.filter(address => {
        const cacheKey = `${chainId}-${address}-${userAddress}`;
        const cached = GLOBAL_CACHE.get(cacheKey);
        return !cached || now - cached.timestamp > CACHE_TTL;
      }).map(address_0 => ({
        address: address_0,
        abi: erc721EnumerableAbi,
        functionName: 'balanceOf' as const,
        args: [userAddress],
        chainId: chainId
      }));
      if (batchContracts.length > 0) {
        batches.push({
          contracts: batchContracts,
          multicall: {
            address: multicallAddress,
            abi: multicall3Abi,
            batchSize: BATCH_SIZE
          }
        });
      }
    }
    return batches;
  }, [contractAddresses, userAddress, chainId]);

  // Fetch real-time balances for current batch with debouncing
  const currentBatch = contractBatches[batchIndexRef.current];
  const {
    data: balanceData,
    isLoading,
    refetch
  } = useReadContracts({
    ...(currentBatch || {
      contracts: []
    }),
    query: {
      enabled: enabled && !!currentBatch && currentBatch.contracts.length > 0,
      staleTime: CACHE_TTL,
      gcTime: CACHE_TTL * 2,
      retry: 2,
      retryDelay: 1000
    }
  });

  // Handle successful batch results with debouncing
  useEffect(() => {
    if (!enabled) return;
    if (balanceData && currentBatch) {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      processingTimeoutRef.current = setTimeout(() => {
        // Cache the results
        currentBatch.contracts.forEach((contract, index) => {
          const cacheKey_0 = `${chainId}-${contract.address}-${userAddress}`;
          const currentCache = GLOBAL_CACHE.get(cacheKey_0);
          const newBalance = balanceData[index]?.result ? Number(String(balanceData[index].result)) : 0;

          // Only update cache if we got a valid result or if no cache exists
          if (balanceData[index]?.result || !currentCache) {
            GLOBAL_CACHE.set(cacheKey_0, {
              balance: newBalance,
              timestamp: Date.now(),
              optimisticValue: currentCache?.optimisticValue
            });
          }
        });

        // Move to next batch if available
        if (batchIndexRef.current < contractBatches.length - 1) {
          batchIndexRef.current++;
          refetch();
        }
      }, DEBOUNCE_INTERVAL);
    }
  }, [balanceData, currentBatch, chainId, userAddress, contractBatches.length, refetch, enabled]);

  // Helper function to get balance with optimistic updates
  const getBalance = (address_1: Address): {
    balance: number;
    isLoading: boolean;
  } => {
    const cacheKey_1 = `${chainId}-${address_1}-${userAddress}`;
    const cached_0 = GLOBAL_CACHE.get(cacheKey_1);
    const now_0 = Date.now();
    if (cached_0) {
      if (now_0 - cached_0.timestamp <= CACHE_TTL) {
        return {
          balance: cached_0.optimisticValue ?? cached_0.balance,
          isLoading: false
        };
      }
      // Return stale cache value while loading new data to prevent 0 flash
      return {
        balance: cached_0.optimisticValue ?? cached_0.balance,
        isLoading: true
      };
    }

    // If not in cache, check current batch
    if (currentBatch?.contracts) {
      const batchIndex = currentBatch.contracts.findIndex(c => c.address?.toLowerCase?.() === address_1.toLowerCase());
      if (batchIndex !== -1) {
        const result = balanceData?.[batchIndex];
        return {
          balance: result?.result ? Number(String(result.result)) : 0,
          isLoading: isLoading || !result
        };
      }
    }

    // Only return 0 if we have no cached data at all
    return {
      balance: 0,
      isLoading: true
    };
  };

  // Process and compare balances with optimistic updates
  const verifiedBalances = useMemo(() => {
    const balanceMap = new Map<Address, NFTCollectionBalance>();

    // Process API collections
    if (allCollections) {
      allCollections.forEach(collection_3 => {
        const addr_2 = collection_3?.token?.address;
        if (typeof addr_2 !== 'string' || addr_2.length === 0) {
          return;
        }
        const contractAddress = addr_2.toLowerCase() as Address;
        const apiBalance = Number(collection_3.value) || 0;
        const {
          balance: realTimeBalance,
          isLoading: isContractLoading
        } = getBalance(contractAddress);
        const hasDiscrepancy = apiBalance !== realTimeBalance;
        balanceMap.set(contractAddress, {
          contractAddress,
          apiBalance,
          realTimeBalance,
          hasDiscrepancy,
          isLoading: isContractLoading,
          enabled: true
        });
      });
    }

    // For Shibarium, also process marketplace collections not in API response
    if (chainId === shibarium.id) {
      const marketplaceCollections_0 = getMarketplaceCollections(chainId);
      marketplaceCollections_0.forEach(collection_4 => {
        if (!collection_4.address) return;
        const contractAddress_0 = collection_4.address.toLowerCase() as Address;

        // Skip if already processed from API collections
        if (balanceMap.has(contractAddress_0)) return;
        const {
          balance: realTimeBalance_0,
          isLoading: isContractLoading_0
        } = getBalance(contractAddress_0);
        // API didn't return this collection, so apiBalance is 0
        // Only add if user has a non-zero balance (they actually own NFTs from this collection)
        if (realTimeBalance_0 > 0 || isContractLoading_0) {
          balanceMap.set(contractAddress_0, {
            contractAddress: contractAddress_0,
            apiBalance: 0,
            realTimeBalance: realTimeBalance_0,
            hasDiscrepancy: realTimeBalance_0 > 0,
            isLoading: isContractLoading_0,
            enabled: true
          });
        }
      });
    }
    return balanceMap;
  }, [allCollections, balanceData, isLoading, chainId]);

  // Check if any collection has a discrepancy
  const hasAnyDiscrepancy = useMemo(() => {
    return Array.from(verifiedBalances.values()).some(balance => balance.hasDiscrepancy);
  }, [verifiedBalances]);
  return {
    verifiedBalances,
    hasAnyDiscrepancy,
    isLoading: isLoading && batchIndexRef.current < contractBatches.length,
    refetch
  };
};